// Staff softphone web push (roadmap #41). Ported from qr-phone-frontend
// firebase.service.tsx (messaging half) + App.jsx (foreground onMessage + the
// service-worker message bridge). The FCM contract is unchanged: the token is
// registered with POST /users/fcmtoken/update and silent notifications carry the
// same action names (FCM-INCOMING-CALL, FCM-KILL-NOTIFICATION-CALL,
// FCM-LOGOUT-SESSION) and the same payload fields the backend already sends.
import { getMessaging, getToken, onMessage } from 'firebase/messaging'
import { child, get, ref } from 'firebase/database'
import { app, authReady, database } from '../core/firebase'
import { globals } from '../core/globals'
import { httpRequest } from '../core/http'
import { updateCallStatusByUserId } from '../core/callStatus'
import { setStoredFcmToken, staffLogout } from './staffAuth'

// Web push certificate (VAPID public key). It is issued PER FIREBASE PROJECT, so it has
// to travel with the rest of the per-environment Firebase configuration (roadmap #12):
// a key from another project makes getToken() fail and the device silently never
// registers for call alerts. Defaults to the production key so a build with no override
// behaves exactly as before. It is a public key by design, not a secret.
const VAPID_KEY =
  import.meta.env.VITE_FIREBASE_VAPID_KEY ||
  'BF4X6ryYiTfK4dqPsQcfAiceSjmNXynFQPSoPcvEX-TGgaTyR8Ti6mxHFTIZaDvCBL1n2uGPr54fcObjPEWrKpw'

let messaging: ReturnType<typeof getMessaging> | null = null
const getMessagingInstance = () => {
  if (!messaging) messaging = getMessaging(app)
  return messaging
}

// SEND THE DEVICE FCM TOKEN TO THE SERVER SO THIS WEB DEVICE RECEIVES CALL NOTIFICATIONS.
export const updateFCMToken = async (userId: number, token: string) => {
  return httpRequest('users/fcmtoken/update', 'POST', { token, user_id: userId, platform: 'web' })
}

const fetchFcmToken = async (userId: number) => {
  try {
    // The registration MUST be passed explicitly (roadmap #101): with no registration the
    // messaging library looks for '/firebase-messaging-sw.js' at the site ROOT, which does not
    // exist for a build served from a base path such as /caller-beta/.
    const serviceWorkerRegistration = await getMessagingRegistration()
    const token = await getToken(getMessagingInstance(), {
      vapidKey: VAPID_KEY,
      ...(serviceWorkerRegistration ? { serviceWorkerRegistration } : {}),
    })
    if (token) {
      setStoredFcmToken(token)
      await updateFCMToken(userId, token)
    }
  } catch (err) {
    console.log('Error getting FCM token:', err)
  }
}

// REQUEST NOTIFICATION PERMISSION (IF NEEDED) AND REGISTER THE TOKEN. WITHOUT THIS THE
// STAFF DEVICE NEVER RECEIVES INCOMING-CALL PUSHES.
export const registerFcmToken = (userId: number) => {
  if (typeof Notification === 'undefined') return
  if (Notification.permission !== 'granted') {
    Notification.requestPermission().then((permission) => {
      if (permission === 'granted') fetchFcmToken(userId)
      else console.log('Notification permission denied', permission)
    })
  } else {
    fetchFcmToken(userId)
  }
}

// REGISTER THE BACKGROUND MESSAGING SERVICE WORKER (SHOWS THE OS NOTIFICATION AND BRIDGES
// THE ACTION BACK TO THE APP WHILE NO TAB IS FOCUSED).
//
// The worker ships in public/, so it is published under the application's base path, NOT at the
// site root: in production this application is served from /caller-beta/. Registering the root
// path requested a file that does not exist, the registration failed, and the bridge that opens
// the in-app incoming-call screen never ran, leaving an operating-system notification the
// application could not act on (roadmap #101). BASE_URL is '/' in development and the deployment
// base in production, and always ends in a slash.
const SERVICE_WORKER_URL = `${import.meta.env.BASE_URL}firebase-messaging-sw.js`

// One shared registration: both the explicit call below and the token request need the same one,
// and registering twice for the same scope is wasteful.
let registrationPromise: Promise<ServiceWorkerRegistration | null> | null = null

const getMessagingRegistration = (): Promise<ServiceWorkerRegistration | null> => {
  if (!('serviceWorker' in navigator)) return Promise.resolve(null)
  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker
      .register(SERVICE_WORKER_URL)
      .then((registration) => {
        console.log('Messaging service worker registered:', registration.scope)
        return registration
      })
      .catch((err) => {
        console.error('Service worker registration failed:', err)
        // Do not cache the failure: a later attempt (e.g. after a redeploy) should retry.
        registrationPromise = null
        return null
      })
  }
  return registrationPromise
}

export const registerMessagingServiceWorker = () => {
  void getMessagingRegistration()
}

// HANDLE A SILENT/BACKGROUND NOTIFICATION (FOREGROUND PATH). MIRRORS
// processSilentNotification in the previous client: it primes the shared call state and
// routes the staff to the incoming-call screen.
export const processSilentNotification = async (data: any, navigate: (path: string, opts?: any) => void) => {
  const action: string = data?.action

  if (action === 'FCM-LOGOUT-SESSION') {
    staffLogout()
    navigate('/staff')
    return
  }

  if (action === 'FCM-KILL-NOTIFICATION-CALL' && globals.user_id === data.user_id) {
    globals.callerSocketID = null
    globals.callerUserName = null
    globals.callerDetail = null
    globals.call_id = null
    globals.callStaffData = null
    globals.notification_received_time = null
    globals.userFromNotification = false
    navigate('/staff/dashboard')
    return
  }

  if (action === 'FCM-INCOMING-CALL') {
    globals.callerSocketID = data.body
    globals.callerUserName = data.name
    globals.callerDetail = data.callerDetail ? JSON.parse(data.callerDetail) : null
    globals.call_id = data.call_id
    globals.maxResponseSeconds = data.maxResponseSeconds
    globals.enabledDirectCall = data?.enabledDirectCall
    globals.isThisDefaultStaffNotified = data.isThisDefaultStaffNotified
    globals.callStaffData = data.staffs
    globals.notification_received_time = data.time
    globals.userFromNotification = false
    await updateCallStatusByUserId(data.call_id, 'FCMPR', data.user_id)

    if (globals.currentScreenName !== 'calling') {
      navigate('/staff/incomingcall', { state: { callArrivedAt: Date.now() } })
    }
  }
}

// FOREGROUND MESSAGES (TAB OPEN): ROUTE THEM THROUGH THE SAME SILENT-NOTIFICATION HANDLER.
export const subscribeForegroundMessages = (navigate: (path: string, opts?: any) => void) => {
  onMessage(getMessagingInstance(), (notification) => {
    const data = notification?.data
    if (data && (typeof data.type !== 'undefined' || typeof data.action !== 'undefined')) {
      processSilentNotification(data, navigate)
    }
  })
}

// PRIME THE SHARED CALL STATE FROM AN INCOMING-CALL NOTIFICATION PAYLOAD AND MARK THE CALL AS
// RECEIVED (FCMPR). SHARED BY THE PLAIN BRIDGE (open the ringing screen) AND THE ACCEPT/REJECT
// NOTIFICATION ACTIONS (#55).
const primeIncomingCallFromPayload = async (payload: any) => {
  globals.callerSocketID = payload.body
  globals.callerUserName = payload.name
  globals.callerDetail = payload.callerDetail ? JSON.parse(payload.callerDetail) : null
  globals.call_id = payload.call_id
  globals.enabledDirectCall = payload?.enabledDirectCall
  globals.isThisDefaultStaffNotified = payload.isThisDefaultStaffNotified
  globals.callStaffData = payload.staffs
  globals.maxResponseSeconds = payload.maxResponseSeconds || globals.maxResponseSeconds
  globals.notification_received_time = payload.time
  globals.userFromNotification = false
  await updateCallStatusByUserId(payload.call_id, 'FCMPR', payload.user_id)
}

// REBUILD AN INCOMING CALL FROM ITS IDENTIFIER ALONE.
//
// The bridge below can only reach an application window that is ALREADY RUNNING. When the
// staff member has no window open, the notification is all there is, and acting on it opens a
// window whose only clue is the call identifier carried in the link. This reads the call
// record and reconstructs the same state the push payload would have primed, so answering
// works from a cold start. Returns false when there is nothing to answer any more — the call
// ended, timed out or another staff member took it — so the caller can say so instead of
// opening a ringing screen for a call that is over.
export const primeIncomingCallFromCallId = async (callId: string): Promise<boolean> => {
  try {
    // The realtime rules require a session; the one established at startup is enough.
    await authReady
    const snapshot = await get(child(ref(database, 'call'), callId))
    const record = snapshot.val()
    if (!record) return false

    // ALREADY RESOLVED? Do not reopen a call that is finished or taken.
    const settled = ['ENDED', 'MISS', 'REJTD', 'ACCPT', 'ANSRD']
    if (settled.indexOf(record.callstatus) !== -1) return false
    if (record.answaredby_fullname) return false

    // Without the guest's connection there is nobody to signal, so answering would strand
    // them on "connecting" — exactly the failure this path exists to fix. The backend writes
    // it onto the record; an older record predating that has none.
    if (!record.callerSocketId) return false

    // The record and the push payload describe the same call with DIFFERENT field names:
    // the record keeps the raw `userinfo` (`qrOrgName`, `qrcodeName`), while the payload
    // carries a `callerDetail` the backend builds from it. The ringing screen reads the
    // payload shape, so the same mapping the backend applies is applied here — otherwise the
    // screen renders with no organisation and no code, which is all the staff member has to
    // recognise who is calling.
    const userinfo = record.userinfo || {}
    globals.callerSocketID = record.callerSocketId
    globals.callerUserName = [userinfo.qrOrgName, userinfo.qrcodeName].filter(Boolean).join(' - ')
    globals.callerDetail = {
      name: userinfo.forminput?.name || '',
      forminput: userinfo.forminput,
      defaultstaff: userinfo.defaultstaff || '',
      lastcalled: userinfo.lastcalled || '',
      lastattendedby: userinfo.lastattendedby || '',
      organization_name: userinfo.qrOrgName,
      qrcode_name: userinfo.qrcodeName,
    }
    globals.call_id = callId
    globals.enabledDirectCall = record.enabledDirectCall
    globals.isThisDefaultStaffNotified = Boolean(record.isDefaultStaffInStaffList)
    // The staff group is on the record too (`staffs`), and the rejection path needs it to
    // notify the other devices. It is serialised because that is the shape the payload
    // delivers and the screen forwards verbatim. An empty list when absent keeps that path
    // working rather than throwing on a parse.
    globals.callStaffData = JSON.stringify(record.staffs || [])
    globals.notification_received_time = String(Date.now())
    globals.userFromNotification = false
    await updateCallStatusByUserId(callId, 'FCMPR', globals.user_id)
    return true
  } catch (e) {
    console.log('could not rebuild the incoming call from its identifier', e)
    return false
  }
}

// BRIDGE MESSAGES POSTED BY THE SERVICE WORKER (BACKGROUND NOTIFICATION INTERACTIONS).
// INCOMING_CALL_ACTION opens the in-app incoming-call screen; INCOMING_CALL_ACCEPT /
// INCOMING_CALL_REJECT (#55) come from the notification's Accept/Decline buttons and drive the
// in-app answer/reject via a navigation flag the ringing screen acts on; STOP_RINGTONE asks the
// screen to stop (another staff answered / the notification was dismissed).
export const subscribeServiceWorkerMessages = (
  navigate: (path: string, opts?: any) => void,
  onStopRingtone: () => void
) => {
  if (!('serviceWorker' in navigator)) return () => {}
  const handler = async (event: MessageEvent) => {
    const data = event.data
    if (!data) return
    const { type, payload, call_id } = data

    if (type === 'STOP_RINGTONE') {
      onStopRingtone()
      return
    }

    if (
      (type === 'INCOMING_CALL_ACTION' ||
        type === 'INCOMING_CALL_ACCEPT' ||
        type === 'INCOMING_CALL_REJECT') &&
      payload &&
      typeof payload === 'object'
    ) {
      await primeIncomingCallFromPayload(payload)
      // The ringing screen reads `autoAccept`/`autoReject` from the navigation state and performs
      // the same accept/reject the in-app buttons do (no protocol duplicated here).
      const autoAccept = type === 'INCOMING_CALL_ACCEPT'
      const autoReject = type === 'INCOMING_CALL_REJECT'
      if (globals.currentScreenName !== 'calling' || autoAccept || autoReject) {
        navigate('/staff/incomingcall', { state: { callArrivedAt: Date.now(), autoAccept, autoReject } })
      }
    } else if (type === 'STOP_RINGTONE_FOR' && call_id && call_id === globals.call_id) {
      onStopRingtone()
    }
  }
  navigator.serviceWorker.addEventListener('message', handler)
  return () => navigator.serviceWorker.removeEventListener('message', handler)
}
